/**
 * MFA Verification — prompts for TOTP code on login when MFA is enrolled.
 * Elevates the session from AAL1 to AAL2.
 */
import { useState, useEffect } from 'react'
import { supabase } from '../../lib/supabase'
import { useNavigate } from 'react-router-dom'
import { useAuth } from './AuthContext'

const inputClass =
  'w-full px-4 py-3 bg-surface-tertiary border border-edge rounded-lg text-ink placeholder:text-ink-subtle focus:outline-none focus:ring-2 focus:ring-brand focus:border-transparent tracking-[0.3em] text-center text-lg font-mono'

export default function MfaVerify() {
  const navigate = useNavigate()
  const { signOut } = useAuth()
  const [code, setCode] = useState('')
  const [error, setError] = useState(null)
  const [loading, setLoading] = useState(false)
  const [factorId, setFactorId] = useState(null)

  useEffect(() => {
    async function loadFactor() {
      const { data, error: listError } = await supabase.auth.mfa.listFactors()
      if (listError) {
        setError(listError.message)
        return
      }
      const totp = data.totp?.[0]
      if (totp) {
        setFactorId(totp.id)
      } else {
        navigate('/mfa/enroll', { replace: true })
      }
    }
    loadFactor()
  }, [navigate])

  async function handleVerify(e) {
    e.preventDefault()
    if (!factorId) return
    setError(null)
    setLoading(true)
    try {
      const { data: challenge, error: challengeError } =
        await supabase.auth.mfa.challenge({ factorId })
      if (challengeError) throw challengeError

      const { error: verifyError } = await supabase.auth.mfa.verify({
        factorId,
        challengeId: challenge.id,
        code,
      })
      if (verifyError) throw verifyError

      // mfa.verify() persists the AAL2 session to storage and triggers
      // onAuthStateChange internally. Do NOT call getSession() here —
      // it deadlocks with the in-flight auth state change.
      // A full page reload lets AuthContext initialize fresh with the
      // stored AAL2 session.
      window.location.replace('/dashboard')
      return
    } catch (err) {
      setError(err.message || 'Invalid code. Please try again.')
      setCode('')
    } finally {
      setLoading(false)
    }
  }

  async function handleSignOut() {
    await signOut()
    navigate('/login', { replace: true })
  }

  return (
    <div className="min-h-screen bg-surface flex items-center justify-center px-4 py-6 sm:py-10">
      <div className="w-full max-w-md">
        <div className="text-center mb-6 sm:mb-8">
          <h1 className="text-2xl sm:text-3xl font-bold text-ink">Two-Factor Authentication</h1>
          <p className="text-ink-muted mt-2">Enter the code from your authenticator app</p>
        </div>

        <form onSubmit={handleVerify} className="bg-surface-secondary rounded-xl p-5 sm:p-8 border border-edge-subtle">
          {error && (
            <div className="bg-danger/40 border border-danger-border rounded-lg p-3 mb-6 text-danger-fg text-sm">
              {error}
            </div>
          )}

          <div className="mb-6">
            <label className="block text-sm font-medium text-ink-muted mb-2">
              6-digit verification code
            </label>
            <input
              type="text"
              inputMode="numeric"
              autoComplete="one-time-code"
              maxLength={6}
              value={code}
              onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
              className={inputClass}
              placeholder="000000"
              required
              autoFocus
            />
          </div>

          <button
            type="submit"
            disabled={loading || code.length !== 6 || !factorId}
            className="w-full py-3 bg-brand hover:opacity-90 disabled:opacity-50 text-ink-onAccent font-semibold rounded-lg transition"
          >
            {loading ? 'Verifying...' : 'Verify'}
          </button>

          <div className="text-center mt-4">
            <button
              type="button"
              onClick={handleSignOut}
              className="text-sm text-ink-muted hover:text-ink"
            >
              Sign in with a different account
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}
